/**
 * MotivationSpecEditor — read+edit UI for a screen's `aiMeta.motivationSpec`
 * (the @segmently/schema-models ScreenMotivationSpec). Embedded inside AiMetaSection.
 *
 * The stored/schema shape uses `class` (the same key the engine and screen-schema-v2
 * use), so this editor reads and writes `aiMeta.motivationSpec.class`. Writes go through
 * the editor's path-based `onChange(path, value)` exactly like the other V2 editor sections.
 *
 * Enum values mirror the schema-models enums (ScreenMotivationClass /
 * ScreenMotivationProofType / valence) and the Simonov needs catalog
 * (src/modules/motivation-economics/needs/catalog.ts → listNeedIds). They are
 * inlined here (small, stable taxonomy) to keep this a pure presentational leaf.
 */

import React from 'react';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Stack,
  Chip,
} from '@mui/material';

export interface MotivationSpec {
  class?: string;
  targetsNeed?: string;
  valence?: 'preservation' | 'growth';
  reflectsJtbd?: boolean;
  socialProof?: { type?: string; intensity?: number };
  justified?: boolean;
  sensitive?: boolean;
  relevant?: boolean;
  probabilityLift?: number;
  goalGC?: number;
}

interface MotivationSpecEditorProps {
  spec?: MotivationSpec;
  /** Path-based writer from the V2 editor (e.g. onChange('aiMeta.motivationSpec.class', v)). */
  onChange: (path: string, value: unknown) => void;
  disabled?: boolean;
}

// ScreenMotivationClass (@segmently/schema-models) — cost/gain/neutral classes.
const CLASS_OPTIONS = [
  'cost:data',
  'cost:decision',
  'cost:education',
  'gain:proof',
  'gain:value',
  'gain:progress',
  'neutral',
] as const;

// Simonov needs catalog ids (motivation-economics/needs/catalog.ts → listNeedIds()).
const NEED_OPTIONS = [
  'vital-safety',
  'vital-economy-of-effort',
  'vital-vitality',
  'social-belonging',
  'social-status',
  'social-norms',
  'ideal-knowledge',
  'ideal-novelty',
  'ideal-meaning',
  'competence-mastery',
  'competence-tools',
  'overcoming-resistance',
] as const;

// ScreenMotivationProofType (@segmently/schema-models).
const PROOF_OPTIONS = ['review', 'rating', 'studyCase', 'bigNumber', 'award'] as const;

const isGain = (cls?: string): boolean => Boolean(cls && cls.startsWith('gain'));
const isCost = (cls?: string): boolean => Boolean(cls && cls.startsWith('cost'));

export const MotivationSpecEditor: React.FC<MotivationSpecEditorProps> = ({ spec, onChange, disabled }) => {
  const set = (field: keyof MotivationSpec, value: unknown) =>
    onChange(`aiMeta.motivationSpec.${field}`, value);

  const cls = spec?.class ?? '';
  const valence = spec?.valence ?? '';
  const proofType = spec?.socialProof?.type ?? '';

  // class chip colour follows the cost/gain semantics (designer.md): gain=success, cost=warning.
  const classColor = isGain(cls) ? 'success' : isCost(cls) ? 'warning' : 'default';

  const boolField = (field: keyof MotivationSpec, label: string, testid: string) => (
    <FormControlLabel
      control={
        <Checkbox
          size="small"
          checked={Boolean(spec?.[field])}
          onChange={(e) => set(field, e.target.checked)}
          disabled={disabled}
          inputProps={{ 'data-testid': testid } as React.InputHTMLAttributes<HTMLInputElement>}
        />
      }
      label={label}
    />
  );

  return (
    <Box data-testid="motivation-spec-section" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="subtitle2">Motivation Spec</Typography>
        {cls && <Chip size="small" color={classColor as any} label={cls} />}
        {valence && (
          <Chip
            size="small"
            variant="outlined"
            color={valence === 'growth' ? 'success' : 'info'}
            label={valence}
          />
        )}
      </Stack>

      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
        <TextField
          select
          size="small"
          label="Class"
          value={cls}
          disabled={disabled}
          onChange={(e) => set('class', e.target.value || undefined)}
          sx={{ minWidth: 170 }}
          data-testid="motivation-spec-class"
        >
          <MenuItem value=""><em>— unset —</em></MenuItem>
          {CLASS_OPTIONS.map((c) => (
            <MenuItem key={c} value={c}>{c}</MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label="Targets need"
          value={spec?.targetsNeed ?? ''}
          disabled={disabled}
          onChange={(e) => set('targetsNeed', e.target.value || undefined)}
          sx={{ minWidth: 210 }}
          data-testid="motivation-spec-targets-need"
        >
          <MenuItem value=""><em>— unset —</em></MenuItem>
          {NEED_OPTIONS.map((n) => (
            <MenuItem key={n} value={n}>{n}</MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label="Valence"
          value={valence}
          disabled={disabled}
          onChange={(e) => set('valence', e.target.value || undefined)}
          sx={{ minWidth: 150 }}
          data-testid="motivation-spec-valence"
        >
          <MenuItem value=""><em>— unset —</em></MenuItem>
          <MenuItem value="preservation">preservation</MenuItem>
          <MenuItem value="growth">growth</MenuItem>
        </TextField>
      </Stack>

      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
        <TextField
          select
          size="small"
          label="Social proof"
          value={proofType}
          disabled={disabled}
          onChange={(e) =>
            set('socialProof', e.target.value ? { type: e.target.value, intensity: spec?.socialProof?.intensity ?? 1 } : undefined)
          }
          sx={{ minWidth: 160 }}
          data-testid="motivation-spec-social-proof"
        >
          <MenuItem value=""><em>none</em></MenuItem>
          {PROOF_OPTIONS.map((p) => (
            <MenuItem key={p} value={p}>{p}</MenuItem>
          ))}
        </TextField>

        <TextField
          type="number"
          size="small"
          label="Prob. lift"
          value={spec?.probabilityLift ?? ''}
          disabled={disabled}
          onChange={(e) => set('probabilityLift', e.target.value === '' ? undefined : Number(e.target.value))}
          inputProps={{ step: 0.05, min: 0, max: 1, 'data-testid': 'motivation-spec-probability-lift' }}
          sx={{ width: 120 }}
        />

        <TextField
          type="number"
          size="small"
          label="Goal GC"
          value={spec?.goalGC ?? ''}
          disabled={disabled}
          onChange={(e) => set('goalGC', e.target.value === '' ? undefined : Number(e.target.value))}
          inputProps={{ step: 0.1, min: 0, 'data-testid': 'motivation-spec-goal-gc' }}
          sx={{ width: 120 }}
        />
      </Stack>

      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
        {boolField('reflectsJtbd', 'Reflects JTBD', 'motivation-spec-reflects-jtbd')}
        {boolField('justified', 'Justified', 'motivation-spec-justified')}
        {boolField('relevant', 'Relevant', 'motivation-spec-relevant')}
        {boolField('sensitive', 'Sensitive', 'motivation-spec-sensitive')}
      </Stack>
    </Box>
  );
};
